import React, { useMemo, useState, useEffect } from "react";
import { Route, Navigation, MapPin, Clock, CheckCircle2, PlayCircle, Sun } from "lucide-react";

const isToday = (iso) => {
  const d = new Date(iso); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const dest = (it) => (it.site_gps?.lat ? `${it.site_gps.lat},${it.site_gps.lng}` : [it.adresse, it.ville].filter(Boolean).join(", "));
const gmapsTo = (it, from) => `https://www.google.com/maps/dir/?api=1${from ? `&origin=${from}` : ""}&destination=${encodeURIComponent(dest(it))}&travelmode=driving`;
const gmapsTour = (stops, from) => {
  const pts = stops.map(dest);
  const last = pts[pts.length - 1];
  const wps = pts.slice(0, -1).map(encodeURIComponent).join("|");
  return `https://www.google.com/maps/dir/?api=1${from ? `&origin=${from}` : ""}&destination=${encodeURIComponent(last)}${wps ? `&waypoints=${wps}` : ""}&travelmode=driving`;
};
const km = (a, b) => {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};
const time = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const STATUS = {
  planifiee: { label: "À faire", cls: "bg-neutral-200 text-neutral-700", icon: Clock },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-700", icon: PlayCircle },
  terminee: { label: "Terminée", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const MyDay = ({ items, renderActions }) => {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { timeout: 8000 });
  }, []);
  const today = useMemo(() => items.filter((i) => isToday(i.date)).sort((a, b) => new Date(a.date) - new Date(b.date)), [items]);
  const remaining = today.filter((i) => i.status !== "terminee");
  const from = pos ? `${pos.lat},${pos.lng}` : "";
  const label = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="collab-myday">
      <div className="bg-neutral-950 text-white rounded-2xl p-5 mb-4">
        <p className="text-xs text-neutral-400 flex items-center gap-1.5 capitalize"><Sun size={13} /> {label}</p>
        <h2 className="font-heading font-extrabold text-2xl mt-1">Ma journée</h2>
        <p className="text-sm text-neutral-300 mt-1" data-testid="myday-summary">
          {today.length === 0 ? "Aucune intervention aujourd'hui." : `${today.length} intervention${today.length > 1 ? "s" : ""} · ${today.filter((i) => i.status === "terminee").length} terminée${today.filter((i) => i.status === "terminee").length > 1 ? "s" : ""} · ${remaining.length} restante${remaining.length > 1 ? "s" : ""}`}
        </p>
        {remaining.length > 1 && (
          <a href={gmapsTour(remaining, from)} target="_blank" rel="noreferrer" data-testid="myday-tour-btn"
            className="mt-3 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Route size={15} /> Itinéraire complet ({remaining.length} étapes)
          </a>
        )}
        {!pos && <p className="text-[11px] text-neutral-500 mt-2">Activez la géolocalisation pour un itinéraire depuis votre position.</p>}
      </div>

      {today.length === 0 ? (
        <p className="text-sm text-neutral-500 bg-white rounded-xl p-5 text-center">Profitez-en : rien de planifié aujourd'hui.</p>
      ) : (
        <ol className="relative border-l-2 border-neutral-200 ml-3 space-y-4">
          {today.map((it, idx) => {
            const st = STATUS[it.status] || STATUS.planifiee;
            const Icon = st.icon;
            const prev = idx > 0 ? today[idx - 1] : null;
            const legKm = it.site_gps?.lat && (prev ? prev.site_gps?.lat && km(prev.site_gps, it.site_gps) : pos && km(pos, it.site_gps));
            return (
              <li key={it.id} className="ml-5 relative" data-testid={`myday-step-${it.id}`}>
                <span className={`absolute -left-[31px] top-4 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${it.status === "terminee" ? "bg-emerald-500 text-white" : it.status === "en_cours" ? "bg-amber-400 text-neutral-900" : "bg-neutral-900 text-white"}`}>{idx + 1}</span>
                <div className="bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-lg text-neutral-900">{time(it.date)}</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${st.cls}`}><Icon size={12} /> {st.label}</span>
                  </div>
                  <p className="font-semibold text-neutral-900 mt-1">{it.client_nom}</p>
                  <p className="text-sm text-neutral-500">{it.prestation || "Nettoyage"}</p>
                  <p className="text-sm text-neutral-600 flex items-start gap-1 mt-1"><MapPin size={14} className="mt-0.5 shrink-0" /> {[it.adresse, it.ville].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
                  {legKm ? <p className="text-xs text-neutral-400 mt-1">≈ {legKm.toFixed(1)} km {prev ? "depuis l'étape précédente" : "depuis votre position"}</p> : null}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {it.status !== "terminee" && (
                      <a href={gmapsTo(it, from)} target="_blank" rel="noreferrer" data-testid={`myday-route-${it.id}`}
                        className="inline-flex items-center gap-1.5 border border-brand text-brand hover:bg-brand/5 text-sm font-semibold px-3 py-2 rounded-md">
                        <Navigation size={14} /> Itinéraire
                      </a>
                    )}
                  </div>
                  {it.status !== "terminee" && renderActions?.(it)}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default MyDay;
